import { useState } from "react";
import { CmsEmptyState } from "@/components/ResearchCms";
import { capturedOnly } from "../../utils/capturedOnly";
import {
  getOriginalSubmissionState,
  submissionDocumentOf,
} from "../../utils/originalSubmissionState";
import type {
  OriginalSubmissionState,
  SubmissionDocument,
} from "../../utils/originalSubmissionState";
import { buildLegacySections } from "../../utils/originalSubmissionSections";
import type { SubmissionSection } from "../../utils/originalSubmissionSections";
import { DocumentLink } from "./DocumentLink";
import { FilterChips } from "./FilterChips";
import styles from "./OriginalSubmissionTab.module.css";
import { SubmissionSections } from "./SubmissionSections";
import { useOriginalSubmission } from "./useOriginalSubmission";

type OriginalSubmissionTabProps = {
  docParseResultId: string | null;
  open: boolean;
  templateType: string | null;
};

type FieldFilter = "all" | "captured";

const FIELD_FILTERS: { id: FieldFilter; label: string }[] = [
  { id: "all", label: "All fields" },
  { id: "captured", label: "Captured only" },
];

const NOT_AVAILABLE_MESSAGE = "Original submission data is not available for this entry";

function Callout({
  title,
  message,
  document,
}: {
  title: string;
  message: string;
  document: SubmissionDocument | null;
}) {
  return (
    <div className={styles.callout}>
      <strong className={styles.calloutTitle}>{title}</strong>
      <p className={styles.calloutText}>{message}</p>
      <DocumentLink document={document} />
    </div>
  );
}

function sectionsFor(
  state: Extract<OriginalSubmissionState, { kind: "sectionsLegacy" }>,
  templateType: string | null,
): SubmissionSection[] {
  return buildLegacySections({
    submissionContext: state.result.submissionContext,
    parsedAt: state.result.parsedAt,
    templateType,
    data: state.parsedJson,
  });
}

type StateView = {
  templateType: string | null;
  filter: FieldFilter;
  onFilter: (filter: FieldFilter) => void;
};

function renderState(state: OriginalSubmissionState, view: StateView) {
  switch (state.kind) {
    case "loading":
      return <CmsEmptyState loading message="Loading original submission" />;
    case "notAvailable":
      return <CmsEmptyState message={NOT_AVAILABLE_MESSAGE} />;
    case "error":
      return (
        <div role="alert" className={styles.error}>
          The original submission could not be loaded. Try again shortly.
        </div>
      );
    case "failed":
      return (
        <Callout
          title="Submission could not be read"
          message="The parser failed on this submission."
          document={state.document}
        />
      );
    case "notFinished":
      return (
        <Callout
          title="Parsing is not complete"
          message="The parser is still working on this submission."
          document={state.document}
        />
      );
    default: {
      const sections = sectionsFor(state, view.templateType);
      return (
        <>
          <FilterChips
            label="Filter fields"
            options={FIELD_FILTERS}
            selected={view.filter}
            onSelect={view.onFilter}
          />
          <SubmissionSections
            sections={view.filter === "captured" ? capturedOnly(sections) : sections}
            document={submissionDocumentOf(state.result)}
          />
        </>
      );
    }
  }
}

export function OriginalSubmissionTab({
  docParseResultId,
  open,
  templateType,
}: OriginalSubmissionTabProps) {
  const load = useOriginalSubmission({ docParseResultId, open });
  const state = getOriginalSubmissionState({ docParseResultId, load });
  const [filter, setFilter] = useState<FieldFilter>("all");

  return (
    <div className={styles.tab}>
      {renderState(state, { templateType, filter, onFilter: setFilter })}
    </div>
  );
}
